import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { languageGroups, getLanguageDisplayName, sortLanguages } from '../../utils/languageUtils';

interface LanguageSelectProps {
  selectedLanguages: string[];
  onChange: (languages: string[]) => void;
}

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  selectedLanguages,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectLanguage = (code: string) => {
    onChange([code]); // Only allow single selection
    setIsOpen(false); // Close dropdown after selection
  };

  // Get unique language codes and sort them
  const uniqueLanguages = Array.from(new Set(Object.values(languageGroups).flat()));
  const sortedLanguages = sortLanguages(uniqueLanguages);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <div className="flex items-center">
          <Globe className="w-5 h-5 text-gray-400 mr-2" />
          <span className="text-sm truncate">
            {selectedLanguages.length === 0
              ? 'Select language'
              : getLanguageDisplayName(selectedLanguages[0])}
          </span>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-2 w-full bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 py-1 max-h-96 overflow-y-auto">
          {sortedLanguages.map(code => (
            <button
              key={code}
              onClick={() => selectLanguage(code)}
              className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-dark-700 ${
                selectedLanguages.includes(code)
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {getLanguageDisplayName(code)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelect;